import { Injectable } from '@angular/core'
import { environment } from '../../environments/environment'
import { HttpClient, HttpHeaders } from '@angular/common/http'
import { LoadingController } from '@ionic/angular';
import { lastValueFrom } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { CookieService } from 'ngx-cookie-service';

@Injectable({
    providedIn: 'root',
})
export class ApiService {

    endpoint = environment.APIEndpoint;
    force_get_data = true;
    cache_mins = 120;
    constructor(private httpClient: HttpClient, private loadingController: LoadingController, private cookieService: CookieService) {
        const val = this.cookieService.get('cache_off');
        if (val === '') {
            this.cookieService.set('cache_off', 'true', 365, '/');
            this.force_get_data = true;
        } else {
            this.force_get_data = val === 'true';
        }
    }

    public updateEndpoint() {
        if (!window.location.hostname.includes("localhost")) {
            this.endpoint = environment.APIEndpointPROD;
        }
        return this.endpoint;
    }

    async post(url, in_data) {
        this.updateEndpoint();
        console.log("POST: " + this.endpoint + url);
        try {
            return await lastValueFrom(this.httpClient.post(this.endpoint + url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async postFullURL(url, in_data) {
        try {
            return await lastValueFrom(this.httpClient.post(url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async get(url) {
        this.updateEndpoint();
        console.log("GET: " + this.endpoint + url);
        try {
            return await lastValueFrom(this.httpClient.get(this.endpoint + url, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    // Short-lived in-memory cache for lists that get re-read over and over in quick succession (e.g. the
    // firm's skills, loaded every time a task is created). Unlike GetTags/GetRoles it isn't stored in
    // localStorage and ignores the cache_off cookie - it only saves repeat calls within a few minutes. Only
    // successful responses are kept, and the same response object is shared, so callers mustn't modify it.
    private memoryCache = new Map<string, { at: number; data: any }>();

    async GetCached(url: string, max_age_ms = 5 * 60 * 1000) {
        const hit = this.memoryCache.get(url);
        if (hit && Date.now() - hit.at < max_age_ms) return hit.data;
        const data: any = await this.get(url);
        if (data?.success) this.memoryCache.set(url, { at: Date.now(), data });
        return data;
    }

    // Drop a cached response early - e.g. after something that changes that list (a new skill created).
    ClearCached(url: string) {
        this.memoryCache.delete(url);
    }

    async getFullURL(url) {
        try {
            return await lastValueFrom(this.httpClient.get(url, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async put(url, in_data) {
        this.updateEndpoint();
        console.log("PUT: " + this.endpoint + url);
        try {
            return await lastValueFrom(this.httpClient.put(this.endpoint + url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async putFullURL(url, in_data) {
        try {
            return await lastValueFrom(this.httpClient.put(url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async delete(url, in_data) {
        this.updateEndpoint();
        console.log("DELETE: " + this.endpoint + url);
        try {
            return await lastValueFrom(this.httpClient.delete(this.endpoint + url, { withCredentials: true, body: in_data }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async deleteFullURL(url, in_data) {
        try {
            return await lastValueFrom(this.httpClient.delete(url, { withCredentials: true, body: in_data }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async patch(url, in_data) {
        this.updateEndpoint();
        console.log("PATCH: " + this.endpoint + url);
        try {
            return await lastValueFrom(this.httpClient.patch(this.endpoint + url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    async patchFullURL(url, in_data) {
        try {
            return await lastValueFrom(this.httpClient.patch(url, in_data, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    private handleError(err, url) {
        console.error("Request Error: ", err);
        return {
            status_code: err?.status || 500,
            success: false,
            message: err?.error?.message || `Something went wrong: ${url}`,
        };
    }


    //Download a file
    async DownloadFile(file) {
        if (!window.location.hostname.includes("localhost")) this.endpoint = environment.APIEndpointPROD;
        window.open(`${this.endpoint}files/download/${file.guid}`, '_blank');
    }
    GetDownloadURL(file) {
        if (!window.location.hostname.includes("localhost")) this.endpoint = environment.APIEndpointPROD;
        return `${this.endpoint}files/download/${file.guid}`;
    }
    async DownloadAsZip(guid_array) {
        if (!window.location.hostname.includes("localhost")) this.endpoint = environment.APIEndpointPROD;
        let guid_string = guid_array.join("~");
        window.open(`${this.endpoint}files/download/zip/${guid_string}`, '_blank');
    }

    async DownloadFilePost(data, file) {
        if (!window.location.hostname.includes("localhost")) this.endpoint = environment.APIEndpointPROD;
        // Set options, including responseType to 'blob' for file download
        const httpOptions = {
            headers: new HttpHeaders({
                // Set any necessary headers here
            }),
            responseType: 'blob' as 'json'  // Cast responseType to 'blob' to tell HttpClient to expect a binary response
        };

        // Send the POST request with data to the server and expect a file in response
        this.httpClient.post(`${this.endpoint}public/email/files/${file.guid}`, data, httpOptions).subscribe(
            (response: any) => {
                // Create a URL for the file
                const blob: Blob = new Blob([response], { type: 'application/octet-stream' });
                const downloadUrl = window.URL.createObjectURL(blob);

                // Create a link to download it
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = file.file_name;  // Set the file name here
                link.click();

                // Clean up by revoking the object URL
                window.URL.revokeObjectURL(downloadUrl);
            },
            error => {
                console.log('Error downloading the file.');
                // Handle any errors encountered during download
            }
        );
    }

    async GenerateSurveyPDF(guid) {
        if (!window.location.hostname.includes("localhost")) this.endpoint = environment.APIEndpointPROD;
        window.open(`${this.endpoint}surveys/pdf/${guid}`, '_blank');
    }

    async getSpecificURL(url) {
        this.updateEndpoint();
        if (window.location.hostname.includes("localhost")) {
            url = url.replace('https://app.fusionworkflow.com/', 'http://localhost:8100/');
        }
        console.log("GET SPECIFIC URL: ", url);
        try {
            return await lastValueFrom(this.httpClient.get(url, { withCredentials: true }));
        } catch (err) {
            return this.handleError(err, url);
        }
    }

    //I want this to upload a file, then return its GUID
    async UploadFile(file: File, folder_guid: string, additional_data: any = null): Promise<string | null> {
        await this.presentLoading(`Uploading ${file.name}`, 99999999999999);
        const post_data = { file_name: file.name, file_size: file.size, is_temp: false, ...additional_data };

        // 1) Ask your API for a SAS upload URL (scoped to this blob) + guid
        const data: any = await this.post(`files/get_upload_url/${folder_guid}`, post_data);
        if (!data?.success) {
            await this.dismissLoading();
            return null;
        }

        const sasUrl: string = data.data[0].upload_url;
        const fileGuid: string = data.data[0].file_guid;

        await this.UploadFileToBlobUrl(file, sasUrl, percent =>
            this.presentLoading(`Uploading ${file.name}: ${percent}%`, 99999999999999)
        );

        this.put(`files/commit_upload/${fileGuid}`, null);
        await this.dismissLoading();
        return fileGuid ?? null;
    }

    // Uploads a File's raw bytes directly to blob storage via an already-issued SAS write URL - a
    // single PUT for files <= 64 MiB, otherwise chunked Put Block + Put Block List (so one giant
    // request isn't required for large files, and progress can be reported). Doesn't touch any
    // app-side file-record bookkeeping (guid/commit/folder placement) - callers needing that layer
    // (e.g. UploadFile above) handle it around this using whatever endpoint issued the SAS URL.
    async UploadFileToBlobUrl(file: File, sasUrl: string, onProgress?: (percent: number) => void): Promise<void> {
        // Helper to add/override query params on the SAS URL
        const withParams = (url: string, params: Record<string, string>) => {
            const u = new URL(url);
            Object.entries(params).forEach(([k, v]) => u.searchParams.set(k, v));
            return u.toString();
        };

        // If file <= 64 MiB, a single PUT is fine; otherwise do block upload.
        const SINGLE_PUT_LIMIT = 64 * 1024 * 1024;

        if (file.size <= SINGLE_PUT_LIMIT) {
            // Single-shot PUT (simplest)
            await lastValueFrom(this.httpClient.put(sasUrl, file, {
                headers: {
                    'x-ms-blob-type': 'BlockBlob',
                    'Content-Type': file.type || 'application/octet-stream'
                }
            }));
            return;
        }

        // Chunked upload via Put Block + Put Block List
        const blockSize = 8 * 1024 * 1024; // 8 MiB blocks (tweak as you like)
        const blockIds: string[] = [];
        const totalBlocks = Math.ceil(file.size / blockSize);

        for (let i = 0; i < totalBlocks; i++) {
            if (onProgress) onProgress(Math.round(((i + 1) / totalBlocks) * 100));
            const start = i * blockSize;
            const end = Math.min(start + blockSize, file.size);
            const chunk = file.slice(start, end);

            // Block IDs must be base64 strings. Fixed-length numeric helps ordering.
            const rawId = `block-${i.toString().padStart(6, '0')}`;
            const blockId = btoa(rawId); // base64
            blockIds.push(blockId);

            const putBlockUrl = withParams(sasUrl, { comp: 'block', blockid: blockId });

            await lastValueFrom(this.httpClient.put(putBlockUrl, chunk, {
                headers: {
                    // No x-ms-blob-type for Put Block
                    'x-ms-version': '2020-10-02' // optional; any recent version your account supports
                }
            }));
        }

        // Commit the block list (finalize the blob)
        const blockListXml =
            `<?xml version="1.0" encoding="utf-8"?>` +
            `<BlockList>${blockIds.map(id => `<Latest>${id}</Latest>`).join('')}</BlockList>`;

        const commitUrl = withParams(sasUrl, { comp: 'blocklist' });

        await lastValueFrom(this.httpClient.put(commitUrl, blockListXml, {
            headers: {
                'Content-Type': 'application/xml',
                'x-ms-blob-content-type': file.type || 'application/octet-stream'
            }
        }));
    }

    loading = null;
    async presentLoading(message, duration) {
        if (this.loading == null) {

            this.loading = await this.loadingController.create({
                cssClass: 'my-custom-class',
                message: message,
                duration: duration
            });
            await this.loading.present();

            // const { role, data } = await this.loading.onDidDismiss()
        }
        else {
            this.loading.message = message;
        }
    }

    async dismissLoading() {
        if (this.loading != null) {
            this.loading.dismiss();
            this.loading = null;
        }
    }

    async GetClients(force_refresh = false) {
        const storedClients = localStorage.getItem('clients');
        const storedTimestamp = localStorage.getItem('clients_last_checked');

        if (force_refresh || !storedClients || !this.isCacheFresh('clients_last_checked') || this.force_get_data) {
            const data: any = await this.get('clients');
            if (data.success) {
                this.saveToCache('clients', 'clients_last_checked', data.data);
            }
            return data;
        }

        const updates: any = await this.get(`updates/clients/${storedTimestamp}`);
        if (updates.success && updates.data && updates.data.length > 0) {
            const fresh: any = await this.get('clients');
            if (fresh.success) {
                this.saveToCache('clients', 'clients_last_checked', fresh.data);
            }
            return fresh;
        }

        const clients = this.decryptFromStorage<any[]>(storedClients) || [];
        return { success: true, message: 'Clients retrieved successfully', data: JSON.parse(JSON.stringify(clients)) };
    }

    async GetCompanies(force_refresh = false) {
        const storedCompanies = localStorage.getItem('companies');
        const storedTimestamp = localStorage.getItem('companies_last_checked');

        if (force_refresh || !storedCompanies || !this.isCacheFresh('companies_last_checked') || this.force_get_data) {
            const data: any = await this.get('companies');
            if (data.success) {
                this.saveToCache('companies', 'companies_last_checked', data.data);
            }
            return data;
        }

        const updates: any = await this.get(`updates/companies/${storedTimestamp}`);
        if (updates.success && updates.data && updates.data.length > 0) {
            const fresh: any = await this.get('companies');
            if (fresh.success) {
                this.saveToCache('companies', 'companies_last_checked', fresh.data);
            }
            return fresh;
        }

        const companies = this.decryptFromStorage<any[]>(storedCompanies) || [];
        return { success: true, message: 'Companies retrieved successfully', data: JSON.parse(JSON.stringify(companies)) };
    }

    async GetGroups(table: string, force_refresh = false) {
        const storageKey = `groups_${table}`;
        const timestampKey = `groups_${table}_last_checked`;
        const storedGroups = localStorage.getItem(storageKey);
        const storedTimestamp = localStorage.getItem(timestampKey);

        if (force_refresh || !storedGroups || !this.isCacheFresh(timestampKey) || this.force_get_data) {
            const data: any = await this.get(`groups/${table}`);
            if (data.success) {
                this.saveToCache(storageKey, timestampKey, data.data);
            }
            return data;
        }

        const updates: any = await this.get(`updates/groups/${table}/${storedTimestamp}`);
        if (updates.success && updates.data && updates.data.length > 0) {
            const fresh: any = await this.get(`groups/${table}`);
            if (fresh.success) {
                this.saveToCache(storageKey, timestampKey, fresh.data);
            }
            return fresh;
        }

        const groups = this.decryptFromStorage<any[]>(storedGroups) || [];
        return { success: true, message: 'Groups retrieved successfully', data: JSON.parse(JSON.stringify(groups)) };
    }

    async GetTags(table: string, force_refresh = false) {
        const storageKey = `tags_${table}`;
        const timestampKey = `tags_${table}_last_checked`;
        const storedTags = localStorage.getItem(storageKey);
        const storedTimestamp = localStorage.getItem(timestampKey);

        if (force_refresh || !storedTags || !this.isCacheFresh(timestampKey) || this.force_get_data) {
            const data: any = await this.get(`tags/${table}`);
            if (data.success) {
                this.saveToCache(storageKey, timestampKey, data.data);
            }
            return data;
        }

        const updates: any = await this.get(`updates/tags/${table}/${storedTimestamp}`);
        if (updates.success && updates.data && updates.data.length > 0) {
            const fresh: any = await this.get(`tags/${table}`);
            if (fresh.success) {
                this.saveToCache(storageKey, timestampKey, fresh.data);
            }
            return fresh;
        }

        const tags = this.decryptFromStorage<any[]>(storedTags) || [];
        return { success: true, message: 'Tags retrieved successfully', data: JSON.parse(JSON.stringify(tags)) };
    }

    async GetRoles(force_refresh = false) {
        const storedRoles = localStorage.getItem('roles');
        const storedTimestamp = localStorage.getItem('roles_last_checked');

        if (force_refresh || !storedRoles || !this.isCacheFresh('roles_last_checked') || this.force_get_data) {
            const data: any = await this.get('firms/clients/roles');
            if (data.success) {
                this.saveToCache('roles', 'roles_last_checked', data.data);
            }
            return data;
        }
        
        const updates: any = await this.get(`updates/clients_roles/${storedTimestamp}`);
        if (updates.success && updates.data && updates.data.length > 0) {
            const fresh: any = await this.get('firms/clients/roles');
            if (fresh.success) {
                this.saveToCache('roles', 'roles_last_checked', fresh.data);
            }
            return fresh;
        }

        const roles = this.decryptFromStorage<any[]>(storedRoles) || [];
        return { success: true, message: 'Roles retrieved successfully', data: JSON.parse(JSON.stringify(roles)) };
    }

    async GetProjectSchedule(url_mod: string, force_refresh = false) {
        const data_to_return: any = await this.get(`projects_schedule?${url_mod}`);
        return data_to_return;
        let table = 'projects_schedule';
        const storageKey = `${table}`;
        const timestampKey = `${table}_last_checked`;
        const urlModKey = `${table}_url_mod`;

        const storedSchedule = localStorage.getItem(storageKey);
        const storedTimestamp = localStorage.getItem(timestampKey);
        const storedUrlMod = localStorage.getItem(urlModKey);
        let loaded_new = false;

        // If url_mod changed, clear localStorage for full refresh
        if (storedUrlMod !== url_mod) {
            localStorage.removeItem(storageKey);
            localStorage.removeItem(timestampKey);
            localStorage.removeItem(urlModKey);
        }

        if (!storedSchedule || storedUrlMod !== url_mod || force_refresh) {
            // No schedule in localStorage, url_mod changed, or force refresh
            const data: any = await this.get(`${table}?${url_mod}`);
            loaded_new = true;
            if (data.success) {
                this.saveToCache(storageKey, timestampKey, data.data);
                localStorage.setItem(urlModKey, url_mod);
            }
            return data;
        }
        else {
            console.log("RETURNING STORED PROJECT SCHEDULE");
        }

        // Parse existing schedules
        let schedules = this.decryptFromStorage<any[]>(storedSchedule) || [];

        // Check for updates since last timestamp
        let got_something = false;

        if (!loaded_new) {
            const lastChecked = storedTimestamp || new Date(0).toISOString();
            const data: any = await this.get(`updates/${table}/${lastChecked}`);

            if (data.success && data.data && data.data.length > 0) {
                got_something = true;
                if (data.data.length > 25) {
                    const data: any = await this.get(`${table}?${url_mod}`);
                    if (data.success) {
                        this.saveToCache(storageKey, timestampKey, data.data);
                        localStorage.setItem(urlModKey, url_mod);
                    }
                    return data;
                }
                // Update each schedule that has changes
                for (const val of data.data) {
                    const guid = val.guid;
                    const schedule_data: any = await this.get(`${table}/${guid}`);
                    if (schedule_data.success) {
                        // Find and update the schedule in the array
                        const index = schedules.findIndex(schedule => schedule.guid === guid);
                        if (index !== -1) {
                            schedules[index] = schedule_data.data[0];
                        } else {
                            // Schedule not found in local array, add it
                            schedules.push(schedule_data.data[0]);
                        }
                    }
                }

                // Update localStorage with new data and timestamp
                localStorage.setItem(storageKey, this.encryptToStorage(schedules));
            }
        }

        // Always update the timestamp
        if (got_something || loaded_new) localStorage.setItem(timestampKey, new Date().toISOString());

        return {
            success: true,
            message: 'Project schedules retrieved successfully',
            data: JSON.parse(JSON.stringify(schedules))
        };
    }


    private isCacheFresh(timestampKey: string): boolean {
        const stored = localStorage.getItem(timestampKey);
        if (!stored) return false;
        let cache_good = Date.now() - new Date(stored).getTime() < this.cache_mins * 60 * 1000;
        if(!cache_good) console.log(`Cache for ${timestampKey} is stale. Last checked at ${stored}`);
        return cache_good;
    }

    // Encrypting a full list is slow and blocks the UI. Skip it entirely when cache_off means the stored copy
    // is never read, and otherwise defer it so the page that asked for the data can render first.
    private saveToCache(storageKey: string, timestampKey: string, data: any) {
        if (this.force_get_data) {
            localStorage.removeItem(storageKey);
            localStorage.removeItem(timestampKey);
            return;
        }
        const save = () => {
            try {
                console.log(`Saving ${storageKey} to localStorage`);
                localStorage.setItem(storageKey, this.encryptToStorage(data));
                localStorage.setItem(timestampKey, new Date().toISOString());
            } catch (e) {
                console.error(`Error saving ${storageKey} to localStorage`, e);
            }
        };
        const idle = (window as any).requestIdleCallback;
        if (idle) idle(save, { timeout: 5000 });
        else setTimeout(save, 1000);
    }

    private encryptToStorage(data: any): string {
        const json = JSON.stringify(data);
        return CryptoJS.AES.encrypt(json, "TESTTODOCHANGE").toString();
    }

    private decryptFromStorage<T>(cipherText: string | null): T | null {
        if (!cipherText) {
            return null;
        }

        try {
            const bytes = CryptoJS.AES.decrypt(cipherText, "TESTTODOCHANGE");
            const decrypted = bytes.toString(CryptoJS.enc.Utf8);
            if (!decrypted) {
                return null;
            }
            return JSON.parse(decrypted) as T;
        } catch (e) {
            console.error('Error decrypting localStorage data', e);
            return null;
        }
    }

}
